import React, { useState } from "react";
import { Modal, useModal } from "./../shared/Modal";
import SelectProofRow from "./../widget/SelectProofRow";

// Wall-side hand-picked proof selector. Visually identical to the widget
// HandPicked modal — same two-column "available / selected" picker — but
// driven by props (no API fetch) and feeds a server-rendered <form> via
// hidden inputs rather than an in-memory React parent state.
//
// The wall edit page is a plain ERB form, so when the user clicks "save"
// in the studio header we need the selection to ride along as form fields.
// We render an <input type="hidden" name="wall[selection][proof_ids][]">
// for each currently-picked proof; the controller's wall_params permits
// `selection: [..., proof_ids: []]`.
const WallHandPicked = ({ availableProofs = [], initSelectedIds = [], onChange }) => {
  const { modalOpen, setModalOpen } = useModal();

  const initialSelected = availableProofs.filter((p) => initSelectedIds.includes(p.id));
  const initialAvailable = availableProofs.filter((p) => !initSelectedIds.includes(p.id));

  // Committed state — what the form will submit.
  const [selected, setSelected] = useState(initialSelected);
  const [available, setAvailable] = useState(initialAvailable);

  // Working state inside the modal — only flushed to committed state on Save.
  const [draftSelected, setDraftSelected] = useState(initialSelected);
  const [draftAvailable, setDraftAvailable] = useState(initialAvailable);

  const openPicker = () => {
    setDraftAvailable(available);
    setDraftSelected(selected);
    setModalOpen(true);
  };

  const addToDraft = (proof) => {
    setDraftAvailable((d) => d.filter((p) => p.uuid !== proof.uuid));
    setDraftSelected((d) => [...d, proof]);
  };

  const removeFromDraft = (proof) => {
    setDraftSelected((d) => d.filter((p) => p.uuid !== proof.uuid));
    setDraftAvailable((d) => [...d, proof]);
  };

  const cancel = () => setModalOpen(false);

  const saveSelection = () => {
    setSelected(draftSelected);
    setAvailable(draftAvailable);
    setModalOpen(false);
    // The React studio saves through the API and has no form to stamp, so
    // hand it the committed ids. The ERB form ignores this and reads the
    // hidden inputs below.
    onChange?.(draftSelected.map((p) => p.id));
  };

  return (
    <>
      <button type="button" onClick={openPicker} className="zz-btn sm pink">
        select proofs ({selected.length})
      </button>

      {selected.map((p) => (
        <input key={p.id} type="hidden" name="wall[selection][proof_ids][]" value={p.id} />
      ))}

      {modalOpen && (
        <Modal modalOpen={modalOpen} setModalOpen={setModalOpen}>
          <div className="relative px-1 sm:px-4 pt-2 pb-4">
            <div className="mb-5">
              <p className="zz-hand text-2xl sm:text-3xl text-sspink leading-none">pick what matters —</p>
              <h2 className="zz-display text-3xl sm:text-4xl font-black leading-[0.95] mt-1">
                select <span className="zz-highlight yellow">proofs</span>.
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Tap to move a proof between available and selected. Save when you're happy.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-4 sm:gap-5">
              <div className="w-full md:w-1/2 flex flex-col min-w-0">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="zz-hand text-xl text-ssgreen leading-none">available —</p>
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{draftAvailable.length}</span>
                </div>
                <div className="zz-panel p-3 sm:p-4 flex flex-col gap-2 max-h-[35vh] md:max-h-[60vh] overflow-y-auto">
                  {draftAvailable.length === 0 ? (
                    <p className="text-sm text-slate-500 italic text-center py-8">nothing left to pick.</p>
                  ) : (
                    draftAvailable.map((proof, index) => (
                      <SelectProofRow
                        key={proof.uuid}
                        initProof={proof}
                        initSelected={false}
                        addNewSelection={addToDraft}
                        index={index}
                      />
                    ))
                  )}
                </div>
              </div>

              <div className="w-full md:w-1/2 flex flex-col min-w-0">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="zz-hand text-xl text-sspink leading-none">selected —</p>
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{draftSelected.length}</span>
                </div>
                <div className="zz-panel p-3 sm:p-4 flex flex-col gap-2 max-h-[35vh] md:max-h-[60vh] overflow-y-auto">
                  {draftSelected.length === 0 ? (
                    <p className="text-sm text-slate-500 italic text-center py-8">nothing picked yet — tap any proof on the left.</p>
                  ) : (
                    draftSelected.map((proof, index) => (
                      <SelectProofRow
                        key={proof.uuid}
                        initProof={proof}
                        initSelected={true}
                        removeNewSelection={removeFromDraft}
                        index={index}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" onClick={cancel} className="zz-link muted text-sm">
                cancel
              </button>
              <button type="button" onClick={saveSelection} className="zz-btn pink">
                save selection ✓
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export { WallHandPicked };
export default WallHandPicked;
